import {includes} from '@benchling/js-utils';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {getLocationPathname} from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  isProjectsSidepanelSection,
  NavPreferences,
  ProjectsSidepanelSection,
  Section,
  SECTIONS,
  SidepanelSection,
  SidepanelExpansionState,
} from 'constants/NavConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {getUnloadableSections} from 'nav/sidepanel/getUnloadableSections';
import {HOMEPAGE_PATH} from 'nav/sidepanel/homepage/HomepageConstants';
import preferences from 'Preferences';
import {getStore} from 'redux-stores/Store';
import * as SidebarConstants from 'sidebar/SidebarConstants';
import {COLLAPSIBLE_WORKFLOW_SCREENS} from 'workflows/WorkflowConstants';

export function fixWorkflowsExpansionState(
  expansionState: SidepanelExpansionState,
  preferOpen: boolean
): SidepanelExpansionState {
  // Some workflows screens (everything not in COLLAPSIBLE_WORKFLOW_SCREENS) only support CLOSED or
  // FULLY_EXPANDED, so adjust that here.
  if (COLLAPSIBLE_WORKFLOW_SCREENS.includes(getStore().getState().models.workflows.viewState.screen)) {
    return expansionState;
  } else if (
    [SidepanelExpansionState.CLOSED, SidepanelExpansionState.FULLY_EXPANDED].includes(expansionState)
  ) {
    return expansionState;
  } else if (preferOpen) {
    return SidepanelExpansionState.FULLY_EXPANDED;
  } else {
    return SidepanelExpansionState.CLOSED;
  }
}

function fixNewHomepageExpansionState(
  expansionState: SidepanelExpansionState,
  preferOpen: boolean
): SidepanelExpansionState {
  // The redesigned homepage only supports CLOSED or FULLY_EXPANDED.
  if ([SidepanelExpansionState.CLOSED, SidepanelExpansionState.FULLY_EXPANDED].includes(expansionState)) {
    return expansionState;
  } else if (preferOpen) {
    return SidepanelExpansionState.FULLY_EXPANDED;
  } else {
    return SidepanelExpansionState.CLOSED;
  }
}

function doesSectionSupportSemiExpandedPlus(section: SidepanelSection): boolean {
  return section === Section.AI;
}

/**
 * Re-map to the correct semi-expanded state if neecssary.
 */
function getFixedSemiExpansionState(
  section: SidepanelSection,
  expansionState: SidepanelExpansionState
): SidepanelExpansionState {
  if (
    expansionState === SidepanelExpansionState.SEMI_EXPANDED_PLUS &&
    !doesSectionSupportSemiExpandedPlus(section)
  ) {
    return SidepanelExpansionState.SEMI_EXPANDED;
  }
  if (
    expansionState === SidepanelExpansionState.SEMI_EXPANDED &&
    doesSectionSupportSemiExpandedPlus(section)
  ) {
    return SidepanelExpansionState.SEMI_EXPANDED_PLUS;
  }
  return expansionState;
}

/**
 * WARNING: Do not call this function in resetState. It has a dependency on redux, which can cause
 * a runtime circular dependency crash.
 */
export function getFixedExpansionStateFromSection(
  section: SidepanelSection,
  expansionState: SidepanelExpansionState,
  preferOpen: boolean
): SidepanelExpansionState {
  expansionState = getFixedSemiExpansionState(section, expansionState);

  if (includes(SidebarConstants.SETTINGS_SECTIONS, section)) {
    // Settings sections don't support the CLOSED or FULLY_EXPANDED states.
    if (expansionState === SidepanelExpansionState.CLOSED) {
      return SidepanelExpansionState.COLLAPSED;
    } else if (expansionState === SidepanelExpansionState.FULLY_EXPANDED) {
      return SidepanelExpansionState.SEMI_EXPANDED;
    } else {
      return expansionState;
    }
  }

  if (expansionState === SidepanelExpansionState.COLLAPSED) {
    // Non-settings sections don't support COLLAPSED states, so return CLOSED.
    return SidepanelExpansionState.CLOSED;
  }

  if (section === SECTIONS.TFI) {
    // TFI doesn't support a FULLY_EXPANDED state.
    if (expansionState === SidepanelExpansionState.FULLY_EXPANDED) {
      return SidepanelExpansionState.SEMI_EXPANDED;
    } else {
      return expansionState;
    }
  } else if (section === SECTIONS.WORKFLOWS) {
    return fixWorkflowsExpansionState(expansionState, preferOpen);
  } else if (section === SECTIONS.HOME) {
    return fixNewHomepageExpansionState(expansionState, preferOpen);
  }
  return expansionState;
}

function getLastOpenedSection(): SidepanelSection {
  if (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT) {
    return SECTIONS.AI;
  }
  return preferences.get(NavPreferences.SIDEPANEL_LAST_OPENED_SECTION) ?? SECTIONS.HOME;
}

function getLastIsClosed(): boolean {
  if (BenchlingConfig.LLM_ENABLE_PUBLIC_AI_TRIAL_TENANT) {
    return false;
  }
  return preferences.get(NavPreferences.SIDEPANEL_IS_OPEN) === false;
}

class SidepanelStore extends Store {
  expansionState!: SidepanelExpansionState;
  isCreateMenuOpen!: boolean;
  isOverflowMenuOpen!: boolean;
  section!: SidepanelSection;

  listedFolderBySection!: {
    [k in SidepanelSection]?: StringIDFolder | null;
  };
  storablesSidepanelRegistryId!: string | null;
  /**
   * Both `PROJECTS` and `PROJECT_ITEMS` represent the Projects sidepanel. This field stores the last viewed
   * Projects sidepanel section, so we can remember and reopen it when the user reopens the Projects
   * sidepanel.
   */
  private projectsSidepanelSection!: ProjectsSidepanelSection;

  override resetState(): void {
    this.isCreateMenuOpen = false;
    this.isOverflowMenuOpen = false;
    this.section = getLastOpenedSection();
    this.expansionState = getLastIsClosed()
      ? SidepanelExpansionState.CLOSED
      : getFixedSemiExpansionState(this.section, SidepanelExpansionState.SEMI_EXPANDED);

    if (getUnloadableSections().includes(this.section)) {
      this.section = SECTIONS.HOME;
    }

    if (this.section === SECTIONS.HOME) {
      const isHomepageRoute = HOMEPAGE_PATH === getLocationPathname();
      this.expansionState =
        !getLastIsClosed() && isHomepageRoute
          ? SidepanelExpansionState.FULLY_EXPANDED
          : SidepanelExpansionState.CLOSED;
    }

    this.listedFolderBySection = {
      [Section.PROJECTS]: null,
      [Section.PROJECT_ITEMS]: null,
      [Section.REGISTRIES]: null,
    };
    this.storablesSidepanelRegistryId = null;
    this.projectsSidepanelSection = Section.PROJECTS;
  }

  getExpansionState(): SidepanelExpansionState {
    return this.expansionState;
  }

  getIsExpanded(): boolean {
    return this.expansionState === SidepanelExpansionState.FULLY_EXPANDED;
  }

  getIsCreateMenuOpen(): boolean {
    return this.isCreateMenuOpen;
  }

  getIsOverflowMenuOpen(): boolean {
    return this.isOverflowMenuOpen;
  }

  getIsOpen(): boolean {
    return this.expansionState !== SidepanelExpansionState.CLOSED;
  }

  getSection(): SidepanelSection | null {
    return this.getIsOpen() ? this.section : null;
  }

  getListedFolder(section: SidepanelSection): StringIDFolder | null {
    return this.listedFolderBySection[section] ?? null;
  }

  getStorablesSidepanelRegistryId(): string | null {
    return this.storablesSidepanelRegistryId;
  }

  /**
   * If the user changed the listed folder within the Projects sidepanel, then remember the section
   * (`PROJECTS` or `PROJECT_ITEMS`) they were last viewing, so that we can bring it back up in
   * `adjustSection_onSidepanelOpen` below.
   *
   * This method should be called whenever `listedFolderBySection` changes.
   */
  private updateProjectsSidepanelSection_onListedFolderChange(listedFolderSection: SidepanelSection): void {
    if (isProjectsSidepanelSection(listedFolderSection)) {
      this.projectsSidepanelSection = listedFolderSection;
    }
  }

  /**
   * Make any last-minute adjustments to the new section that the user wants to open before committing it to
   * the store state
   */
  private adjustSection_onSidepanelOpen(
    oldSection: SidepanelSection,
    newSection: SidepanelSection
  ): SidepanelSection {
    if (!isProjectsSidepanelSection(oldSection) && newSection === Section.PROJECTS) {
      // The Projects sidepanel uses 2 sections: (1) `PROJECTS` for all projects and (2) `PROJECT_ITEMS` for a
      // particular folder. When a user opens the `PROJECTS` section _from another section_, they actually
      // want us to reopen their last Projects sidepanel view, which is `PROJECTS` if they were last viewing
      // all projects or `PROJECT_ITEMS` if they were last viewing a particular folder. This adjusts
      // `PROJECTS` to the section (`PROJECTS` or `PROJECT_ITEMS`) they were last viewing.
      return this.projectsSidepanelSection;
    } else {
      // Call this method because the Projects sidepanel `listedFolderBySection` state could be changing
      this.updateProjectsSidepanelSection_onListedFolderChange(newSection);
      return newSection;
    }
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.OPEN_SIDEPANEL_WITH_SECTION: {
        if (action.section != null) {
          this.section = this.adjustSection_onSidepanelOpen(this.section, action.section);
        }
        const newExpansionState = [
          SidepanelExpansionState.CLOSED,
          SidepanelExpansionState.COLLAPSED,
        ].includes(this.expansionState)
          ? SidepanelExpansionState.SEMI_EXPANDED
          : this.expansionState;
        preferences.set(NavPreferences.SIDEPANEL_IS_OPEN, true);
        this.expansionState = getFixedExpansionStateFromSection(this.section, newExpansionState, true);
        this.emitChange();
        break;
      }
      case EventConstants.SET_SIDEPANEL_SECTION:
        if (action.section != null) {
          this.section = this.adjustSection_onSidepanelOpen(this.section, action.section);
          this.expansionState = getFixedExpansionStateFromSection(this.section, this.expansionState, true);
          this.emitChange();
        }
        break;
      case EventConstants.SET_SIDEPANEL_CLOSED:
        this.expansionState = SidepanelExpansionState.CLOSED;
        preferences.set(NavPreferences.SIDEPANEL_IS_OPEN, false);
        this.emitChange();
        break;

      case EventConstants.SET_SIDEPANEL_FULLY_EXPANDED:
        preferences.set(NavPreferences.SIDEPANEL_IS_OPEN, true);
        this.expansionState = SidepanelExpansionState.FULLY_EXPANDED;
        if (action.section != null) {
          this.section = action.section;
        }

        this.emitChange();
        break;

      case EventConstants.SET_SIDEPANEL_SEMI_EXPANDED:
        if (action.section != null) {
          this.section = action.section;
        }
        this.expansionState = getFixedExpansionStateFromSection(
          this.section,
          SidepanelExpansionState.SEMI_EXPANDED,
          false
        );
        this.emitChange();
        break;

      case EventConstants.SET_SIDEPANEL_COLLAPSED:
        this.expansionState = SidepanelExpansionState.COLLAPSED;
        this.emitChange();
        break;

      case EventConstants.SIDEPANEL_LIST_SINGLE_SOURCE:
        assert(
          action.section !== SECTIONS.STORAGE,
          'SIDEPANEL_LIST_SINGLE_SOURCE should not be used for STORAGE section'
        );
        this.listedFolderBySection[action.section as Section] = action.folder;
        this.updateProjectsSidepanelSection_onListedFolderChange(action.section);
        this.emitChange();
        break;

      case EventConstants.SIDEPANEL_LIST_ALL_SOURCES:
        if (action.section != null) {
          this.section = action.section;
        }
        this.listedFolderBySection[action.section as Section] = null;
        this.updateProjectsSidepanelSection_onListedFolderChange(action.section);
        this.emitChange();
        break;

      case EventConstants.STORABLES_SIDEPANEL_SET_ACTIVE_REGISTRY:
        this.storablesSidepanelRegistryId = action.registryId;
        this.emitChange();
        break;

      case EventConstants.SIDEPANEL_OPEN_CREATE_MENU:
        this.isCreateMenuOpen = true;
        this.emitChange();
        break;

      case EventConstants.SIDEPANEL_CLOSE_CREATE_MENU:
        this.isCreateMenuOpen = false;
        this.emitChange();
        break;

      case EventConstants.SIDEPANEL_OPEN_OVERFLOW_MENU:
        this.isOverflowMenuOpen = true;
        this.emitChange();
        break;

      case EventConstants.SIDEPANEL_CLOSE_OVERFLOW_MENU:
        this.isOverflowMenuOpen = false;
        this.emitChange();
        break;

      case EventConstants.SIDEBAR_OPEN_FOR_FILE_TYPE: {
        if (
          action.fileType === ModelTypeConstants.TEMPLATE ||
          action.fileType === ModelTypeConstants.SUBTEMPLATE ||
          action.fileType === ModelTypeConstants.ANALYSIS_TEMPLATE ||
          action.fileType === ModelTypeConstants.PROCEDURE ||
          action.fileType === ModelTypeConstants.RECIPE_PROCEDURE ||
          action.fileType === ModelTypeConstants.ASSAY_PROCEDURE ||
          action.fileType === ModelTypeConstants.FORM_DEFINITION
        ) {
          // Preserve an explicitly collapsed/closed sidepanel rather than always forcing it open.
          const userCollapsedOrClosed =
            this.expansionState === SidepanelExpansionState.CLOSED ||
            this.expansionState === SidepanelExpansionState.COLLAPSED;

          if (BenchlingConfig.ENABLE_TEMPLATE_COLLECTIONS_IN_WORKSPACE) {
            // When navigating to a template, switch to the template collections workspace section —
            // but only if the user isn't already on an open workspace section they deliberately
            // navigated to (e.g. Search, Projects). Settings sections are always replaced since they
            // are context-specific and would be stale on a template route.
            const currentSectionIsSettings = includes(SidebarConstants.SETTINGS_SECTIONS, this.section);
            const shouldSwitchSection = userCollapsedOrClosed || currentSectionIsSettings;
            if (shouldSwitchSection) {
              this.section = SECTIONS.TEMPLATE_COLLECTIONS;
            }
          } else {
            this.section = SidebarConstants.Section.NOTEBOOK_SETTINGS_TEMPLATE;
          }

          this.expansionState = getFixedExpansionStateFromSection(
            this.section,
            userCollapsedOrClosed ? this.expansionState : SidepanelExpansionState.SEMI_EXPANDED,
            false
          );
          // @ts-expect-error(2345)
        } else if (this.section != null && Object.values(SECTIONS).indexOf(this.section) === -1) {
          this.section = SECTIONS.HOME;
          this.expansionState = getFixedExpansionStateFromSection(this.section, this.expansionState, false);
        }
        this.emitChange();
        break;
      }
      default:
        break;
    }
    return true;
  }
}

export default new SidepanelStore(AppDispatcher);
